import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { environment } from '../../../../environments/environment';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { DocumentResponse } from '../../../../shared/models/enums/documentResponse.enum';
import { DateService } from '../../../../shared/services/date.service';
import { DocumentTitleService } from '../../../../shared/services/documentTitles.service';
import { DocumentsService } from '../../../../shared/services/documents.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { Utils } from '../../../../shared/utils';
import { DocumentDto } from '../document.dto';

@Component({
  selector: 'app-document-details',
  templateUrl: './document-details.component.html',
  standalone: false,
})
export class DocumentDetailsComponent {
  public documentDetailsForm: FormGroup<{
    title: FormControl<number | null>;
    fileName: FormControl<string | null>;
  }>;
  public selectedFile: File | undefined;
  public submitted: boolean = false;
  public titles: DisplayLabel[] = [];
  private beneficiaryId: number;

  private readonly currentDate: Date;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly documentsService: DocumentsService,
    private readonly dateService: DateService,
    private readonly toastService: ToastService,
    private readonly documentTitlesService: DocumentTitleService,
    private readonly authService: AuthService,
  ) {
    this.currentDate = this.dateService.getCurrent();
  }

  public async onInit(beneficiaryId: number, tag: string): Promise<void> {
    this.beneficiaryId = beneficiaryId;
    this.titles = await this.documentTitlesService.findAll(tag);
    const user = await this.authService.getUser();
    if (user.groups.includes('STRUCTURE')) {
      this.titles = this.titles.filter((title) => title.id !== 8);
    }

    this.loadForm(tag);
  }

  private loadForm(tag: string): void {
    this.documentDetailsForm = this.formBuilder.group({
      title: new FormControl<number | null>(undefined, Validators.required),
      fileName: new FormControl<string | null>(undefined, [Validators.required, this.filetypeValidator(tag)]),
    });
  }

  private filetypeValidator(tag: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this.selectedFile) return null;
      return tag === 'rsj' && !['application/pdf', 'image/png', 'image/jpeg'].includes(this.selectedFile.type)
        ? { allowedType: { value: control.value } } // Forbidden mime type on rsj context
        : null;
    };
  }

  public cancel(): void {
    this.documentDetailsForm.reset();
    this.modal.close();
  }

  public onFileChange(event: Event): void {
    this.selectedFile = (event.target as HTMLInputElement)?.files?.[0];
    this.documentDetailsForm.controls.fileName.setValue(this.selectedFile?.name);
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.documentDetailsForm.valid) {
      const fileDto: DocumentDto = {
        titleId: this.documentDetailsForm.controls.title.value,
        fileName: this.selectedFile.name,
        fileData: await this.read(this.selectedFile),
        beneficiaryId: this.beneficiaryId,
      } as DocumentDto;
      if (fileDto.fileData) {
        const result: DocumentResponse = await this.documentsService.saveDocument(fileDto);
        if (result === DocumentResponse.NoError) {
          this.toastService.show('Le document a été ajouté avec succès.', 'success');
          this.modal.close();
        } else if (result === DocumentResponse.FileName) {
          this.toastService.show('Ce nom de fichier existe déjà pour ce bénéficiaire.', 'danger');
        }
      }
    } else {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
  }

  private async read(file: File): Promise<string> {
    const maxFileSize: number = environment.UPLOAD_MAX_FILESIZE ?? 1000 * 1000 * 1.2;
    if (file.size > maxFileSize) {
      this.toastService.show(
        `Taille maximale de fichier autorisée : ${Utils.humanFileSize(maxFileSize, true, 2)} (taille du document = ${Utils.humanFileSize(
          file.size,
          true,
          2,
        )})`,
        'danger',
      );
    } else {
      return new Promise<string>((resolve: (value: string) => void): void => {
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (): void => {
          resolve(reader.result as string);
        };
      });
    }
  }
}
